import { type ComponentType, Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { FeatureGatedRoute } from '@/components/FeatureGatedRoute';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import type { KeepAliveRouteConfig } from '@/components/keep-alive';
import { attemptImportWithReload } from '@/lib/chunk-reload';

// Wraps React.lazy so a stale-chunk failure (typical after a deploy, when
// the user's open tab still references hashes that no longer exist on the
// server) triggers a one-shot reload via attemptImportWithReload. See
// lib/chunk-reload.ts for the full recovery mechanism, including the
// boot-time vite:preloadError handler that catches non-lazy chunk failures.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature so any component (with any prop shape) can be wrapped
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => attemptImportWithReload(factory));
}

// Lazy load pages for code splitting
const CatalogsPage = lazyWithRetry(() => import('@/pages/catalogs/CatalogsPage'));
const CreateCatalogPage = lazyWithRetry(() => import('@/pages/catalogs/CreateCatalogPage'));
const CatalogDetailPage = lazyWithRetry(() => import('@/pages/catalogs/CatalogDetailPage'));
const EditCatalogPage = lazyWithRetry(() => import('@/pages/catalogs/EditCatalogPage'));
// const MetadataServicesPage = lazyWithRetry(() => import('@/pages/metadata-services/MetadataServicesPage'));
// const CreateMetadataServicePage = lazy(
//   () => import('@/pages/metadata-services/CreateMetadataServicePage')
// );
// const MetadataServiceDetailPage = lazy(
//   () => import('@/pages/metadata-services/MetadataServiceDetailPage')
// );
// const EditMetadataServicePage = lazy(
//   () => import('@/pages/metadata-services/EditMetadataServicePage')
// );
const QueryServicesPage = lazyWithRetry(() => import('@/pages/queryservices/QueryServicesPage'));
const CreateQueryServicePage = lazyWithRetry(
  () => import('@/pages/queryservices/CreateQueryServicePage')
);
const QueryServiceDetailPage = lazyWithRetry(
  () => import('@/pages/queryservices/QueryServiceDetailPage')
);
const EditQueryServicePage = lazyWithRetry(
  () => import('@/pages/queryservices/EditQueryServicePage')
);
const ObservabilityPage = lazyWithRetry(() => import('@/pages/observability/ObservabilityPage'));
// WarmPool Pages
const WarmPoolsPage = lazyWithRetry(() => import('@/pages/warmpools/WarmPoolsPage'));
const CreateWarmPoolPage = lazyWithRetry(() => import('@/pages/warmpools/CreateWarmPoolPage'));
const WarmPoolDetailPage = lazyWithRetry(() => import('@/pages/warmpools/WarmPoolDetailPage'));
const EditWarmPoolPage = lazyWithRetry(() => import('@/pages/warmpools/EditWarmPoolPage'));

const QueryHistoryPage = lazyWithRetry(() => import('@/pages/query-history/QueryHistoryPage'));
const QueryHistoryDetailPage = lazyWithRetry(
  () => import('@/pages/query-history/QueryHistoryDetailPage')
);
// Laminar Pages
const LaminarPage = lazyWithRetry(() => import('@/pages/laminar/LaminarPage'));
// Metriq Pages
const MetriqPage = lazyWithRetry(() => import('@/pages/metriq/MetriqPage'));

const ConsentPage = lazyWithRetry(() => import('@/pages/oauth/ConsentPage'));
const QueryEditorV2Page = lazyWithRetry(() => import('@/pages/query-editor/QueryEditorV2Page'));
const AccessControlPage = lazyWithRetry(() => import('@/pages/access-control/AccessControlPage'));
const GroupDetailPage = lazyWithRetry(() => import('@/pages/access-control/GroupDetailPage'));
const RoleDetailPage = lazyWithRetry(() => import('@/pages/access-control/RoleDetailPage'));

// Keep-alive routes: these persist across navigation (hidden with CSS instead of unmounted)
export const keepAliveRoutes: KeepAliveRouteConfig[] = [
  {
    path: '/query-editor',
    component: QueryEditorV2Page,
  },
];
const SettingsPage = lazyWithRetry(() => import('@/pages/settings/SettingsPage'));
const IpAllowlistPage = lazyWithRetry(() => import('@/pages/ip-allowlist/IpAllowlistPage'));
const AccessTokensPage = lazyWithRetry(() => import('@/pages/access-tokens/AccessTokensPage'));
const ServiceAccountsPage = lazyWithRetry(
  () => import('@/pages/service-accounts/ServiceAccountsPage')
);
const ServiceAccountDetailPage = lazyWithRetry(
  () => import('@/pages/service-accounts/ServiceAccountDetailPage')
);
const MachineClientsPage = lazyWithRetry(
  () => import('@/pages/machine-clients/MachineClientsPage')
);
const MachineClientDetailPage = lazyWithRetry(
  () => import('@/pages/machine-clients/MachineClientDetailPage')
);
const UserSettingsPage = lazyWithRetry(() => import('@/pages/user-settings/UserSettingsPage'));
const TableDemoPage = lazyWithRetry(() => import('@/pages/demo/TableDemoPage'));
const DynamicFormPage = lazyWithRetry(() => import('@/pages/DynamicFormPage'));
const FormsPage = lazyWithRetry(() => import('@/pages/FormsPage'));

/**
 * Routes that render without the application shell.
 *
 * The consent screen is a decision surface, not a page in the product. Framing
 * it with the sidebar and top nav invites a mis-click on something consequential
 * and dilutes what is actually being asked. It stays behind the same auth gate:
 * consent is meaningless without knowing who is giving it.
 */
export const OAUTH_CONSENT_PATH = '/oauth/consent';
export const SHELL_LESS_ROUTES: readonly string[] = [OAUTH_CONSENT_PATH];

export function PageLoader() {
  return (
    <div className="p-6">
      {/* Page header skeleton */}
      <div className="mb-6">
        <div className="bg-muted h-6 w-48 animate-pulse rounded" />
        <div className="bg-muted mt-2 h-4 w-72 animate-pulse rounded" />
      </div>
      {/* Toolbar skeleton */}
      <div className="mb-4 flex items-center gap-3">
        <div className="bg-muted h-8 w-32 animate-pulse rounded" />
        <div className="bg-muted h-8 w-32 animate-pulse rounded" />
        <div className="bg-muted ml-auto h-8 w-24 animate-pulse rounded" />
      </div>
      {/* Table rows skeleton */}
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-muted h-10 animate-pulse rounded" />
        ))}
      </div>
    </div>
  );
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* OAuth consent — no ProtectedRoute wrapper: this is not an RBAC
            resource, and the request itself carries what may be granted. */}
        <Route path={OAUTH_CONSENT_PATH} element={<ConsentPage />} />

        {/* Home - redirect to catalogs */}
        <Route path="/" element={<Navigate to="/catalogs" replace />} />

        {/* Catalogs */}
        <Route
          path="/catalogs"
          element={
            <ProtectedRoute resource="catalogs" action="list">
              <CatalogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalogs/create"
          element={
            <ProtectedRoute resource="catalogs" action="create">
              <CreateCatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalogs/detail"
          element={
            <ProtectedRoute resource="catalogs" action="read">
              <CatalogDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/catalogs/edit"
          element={
            <ProtectedRoute resource="catalogs" action="update">
              <EditCatalogPage />
            </ProtectedRoute>
          }
        />

        {/* Metadata Services — commented out, managed via Settings now */}
        {/* <Route
          path="/metadata-services"
          element={
            <ProtectedRoute resource="metadataservices" action="list">
              <MetadataServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metadata-services/create"
          element={
            <ProtectedRoute resource="metadataservices" action="create">
              <CreateMetadataServicePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metadata-services/detail"
          element={
            <ProtectedRoute resource="metadataservices" action="read">
              <MetadataServiceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/metadata-services/edit"
          element={
            <ProtectedRoute resource="metadataservices" action="update">
              <EditMetadataServicePage />
            </ProtectedRoute>
          }
        /> */}

        {/* Query Services */}
        <Route
          path="/queryservices"
          element={
            <ProtectedRoute resource="queryservices" action="list">
              <QueryServicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/queryservices/create"
          element={
            <ProtectedRoute resource="queryservices" action="create">
              <CreateQueryServicePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/queryservices/detail"
          element={
            <ProtectedRoute resource="queryservices" action="read">
              <QueryServiceDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/queryservices/edit"
          element={
            <ProtectedRoute resource="queryservices" action="update">
              <EditQueryServicePage />
            </ProtectedRoute>
          }
        />

        {/* Warm Pools */}
        <Route
          path="/warmpools"
          element={
            <ProtectedRoute resource="warmnodepools" action="list">
              <WarmPoolsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warmpools/create"
          element={
            <ProtectedRoute resource="warmnodepools" action="create">
              <CreateWarmPoolPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warmpools/detail"
          element={
            <ProtectedRoute resource="warmnodepools" action="read">
              <WarmPoolDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warmpools/edit"
          element={
            <ProtectedRoute resource="warmnodepools" action="update">
              <EditWarmPoolPage />
            </ProtectedRoute>
          }
        />

        {/* Query History */}
        <Route path="/query-history" element={<QueryHistoryPage />} />
        <Route path="/query-history/detail" element={<QueryHistoryDetailPage />} />

        <Route
          path="/observability"
          element={
            <FeatureGatedRoute featureFlag="observe">
              <ObservabilityPage />
            </FeatureGatedRoute>
          }
        />

        {/* Query Editor — handled by KeepAliveOutlet */}

        {/* Access Control */}
        <Route
          path="/access-control"
          element={
            <ProtectedRoute resource="roles" action="list">
              <AccessControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control/groups/detail"
          element={
            <ProtectedRoute resource="groups" action="read">
              <GroupDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/access-control/roles/detail"
          element={
            <ProtectedRoute resource="roles" action="read">
              <RoleDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Laminar — Streaming and Jobs */}
        <Route
          path="/jobs/*"
          element={
            <FeatureGatedRoute featureFlag="laminar-jobs">
              <LaminarPage page="jobs" />
            </FeatureGatedRoute>
          }
        />
        <Route
          path="/pipelines"
          element={
            <FeatureGatedRoute featureFlag="laminar">
              <LaminarPage page="pipelines" />
            </FeatureGatedRoute>
          }
        />
        <Route
          path="/pipelines/new"
          element={
            <FeatureGatedRoute featureFlag="laminar">
              <LaminarPage page="pipeline-create" />
            </FeatureGatedRoute>
          }
        />
        <Route
          path="/pipelines/:id"
          element={
            <FeatureGatedRoute featureFlag="laminar">
              <LaminarPage page="pipeline-detail" />
            </FeatureGatedRoute>
          }
        />

        {/* Metriq — single catch-all route. The remote renders its own
            full app shell (sidebar, header, internal routing) inside the
            host's content area. The /* wildcard is required so the host
            router doesn't 404 on metriq's internal paths like /metriq/kpis. */}
        <Route
          path="/metriq/*"
          element={
            <FeatureGatedRoute featureFlag="metriq">
              <MetriqPage />
            </FeatureGatedRoute>
          }
        />

        {/* Settings */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/ip-allowlist" element={<IpAllowlistPage />} />

        {/* Access Tokens - keeping the route for backward compatibility */}
        <Route path="/access-tokens" element={<AccessTokensPage />} />

        {/* Service Accounts */}
        <Route
          path="/service-accounts"
          element={
            <ProtectedRoute resource="serviceaccounts" action="list">
              <ServiceAccountsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/service-accounts/detail"
          element={
            <ProtectedRoute resource="serviceaccounts" action="read">
              <ServiceAccountDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Machine Clients */}
        <Route
          path="/machine-clients"
          element={
            <ProtectedRoute resource="machineclients" action="list">
              <MachineClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/machine-clients/detail"
          element={
            <ProtectedRoute resource="machineclients" action="read">
              <MachineClientDetailPage />
            </ProtectedRoute>
          }
        />

        {/* User Settings */}
        <Route path="/user-settings" element={<UserSettingsPage />} />

        {/* Demo/Development Pages */}
        <Route path="/demo/table" element={<TableDemoPage />} />
        <Route path="/dynamic-form" element={<DynamicFormPage />} />
        <Route path="/forms" element={<FormsPage />} />
      </Routes>
    </Suspense>
  );
}
